import { Route } from "vue-router";
import { AuthenticatedService } from "../abstract/base.service";
import { UploaderService } from "@/services/cdn/uploader.service";
import { ImageUploader } from "@/models/uploaders/image.uploader";
import { ResultUploader } from "@/models/uploaders/result.uploader";
import { ListRequest } from "@/models/grids/list.request";
import OrderBy from "@/models/shared/orderby";
import { ListResponse } from "@/models/grids/list.response";
import {
  SecurityUserCommand,
  CreateUserCommand,
  EditUserCommand,
  EditUserMetadataCommand
} from "@/server/app.commands";
import { UserDto, LanguageDto, MetadataConfiguration } from "@/server/app.dtos";
import {
  GetUsersDtosQuery,
  GetUserDtoQuery,
  GetUsersDtosOrderOptions,
  GetUserMetadataDtoQuery
} from "@/server/app.queries";

export const UsersEndpoint = `api/users`;

export class UsersRequestIncludes {
  parent: boolean = false;
}

export class UsersListRequestFilter extends ListRequest {
  search?: string;
  name?: string;
  email?: string;
  idAppInterface?: number;
  orderBy?: string = "NameAsc";
  includes: UsersRequestIncludes = new UsersRequestIncludes();

  get GetOrderOptions(): OrderBy[] {
    return [
      new OrderBy("Nome Ascendente", "NameAsc"),
      new OrderBy("Nome Descendente", "NameDesc")
    ];
  }

  loadByRoute(route: Route) {
    if (route && route.query) {
      if (route.query.search) {
        this.search = route.query.search as string;
      }
      this.name = route.query.name ? (route.query.name as string) : undefined;
      this.email = route.query.email
        ? (route.query.email as string)
        : undefined;
      if (route.query.idAppInterface) {
        this.idAppInterface = parseInt(route.query.idAppInterface as string);
      }
      if (route.query.orderBy) {
        this.orderBy = route.query.orderBy as string;
      }
    }
  }
}

export class UsersService extends AuthenticatedService {
  GetList(request: GetUsersDtosQuery): Promise<ListResponse<UserDto>> {
    return this.Get<ListResponse<UserDto>>(
      `${this.baseUrl}${UsersEndpoint}?${this.GetQueryStringByObject(request)}`
    );
  }

  GetInternalUsers(language: LanguageDto): Promise<UserDto[]> {
    return new Promise<UserDto[]>((resolve, reject) => {
      const request = new GetUsersDtosQuery();
      request.idAppInterface = 1;
      request.orderBy = GetUsersDtosOrderOptions.NameAsc;
      request.idLanguage = language.idLanguage;
      this.GetList(request)
        .then((response: ListResponse<UserDto>) => resolve(response.data))
        .catch(error => reject(error));
    });
  }

  GetSingle(request: GetUserDtoQuery): Promise<UserDto> {
    return this.Get<UserDto>(
      `${this.baseUrl}${UsersEndpoint}/${
        request.idUser
      }?${this.GetQueryStringByObject(request)}`
    );
  }

  GetProfile(): Promise<UserDto> {
    return this.Get<UserDto>(`${this.baseUrl}api/current/profile`);
  }

  SaveProfile(user: UserDto): Promise<void> {
    return this.Put(`${this.baseUrl}api/current/profile/`, user);
  }

  Create(entity: CreateUserCommand): Promise<number> {
    return this.Post<number>(`${this.baseUrl}${UsersEndpoint}`, entity);
  }

  Edit(command: EditUserCommand): Promise<number> {
    return this.Put<number>(
      `${this.baseUrl}${UsersEndpoint}/${command.idUser}`,
      command
    );
  }

  DeleteById(id: number): Promise<number> {
    return this.Delete(`${this.baseUrl}${UsersEndpoint}/${id}`);
  }

  ChangePassword(command: SecurityUserCommand): Promise<void> {
    return this.Put(`${this.baseUrl}api/current/security/`, command);
  }

  UploadProfilePhoto(rawData: any): Promise<ResultUploader> {
    const uploader = new ImageUploader();
    uploader.folder = "/Users/Photos";
    uploader.image = rawData;
    return new UploaderService().UploadSinglePhoto(uploader);
  }

  GetProfilePhotoSrc(photo: string | null): string | null {
    if (!photo) return null;
    return `${this.cndUrl}${
      photo.startsWith("/") ? photo.substring(1) : photo
    }`;
  }

  GetMetadata(
    request: GetUserMetadataDtoQuery
  ): Promise<MetadataConfiguration> {
    return this.Get<MetadataConfiguration>(
      `${this.baseUrl}${UsersEndpoint}/${
        request.idUser
      }/metadata?${this.GetQueryStringByObject(request)}`
    );
  }

  EditMetadata(command: EditUserMetadataCommand): Promise<number> {
    return this.Put<number>(
      `${this.baseUrl}${UsersEndpoint}/${command.idUser}/metadata`,
      command
    );
  }
}
